// Quota policy binding — the budget's GL 210 (Diocesan Quota) forecast is
// autopopulated from a saved Quota model policy run instead of the retired
// legacy calculator. The binding (policy version + assessment year) persists
// in budget settings; the run's calendar-year total is split across diocesan
// financial years here.

export type QuotaBinding = {
  /** Immutable policy version from the Quota workspace (Policy & Run). */
  policyVersion: string;
  /** Calendar year the quota schedule is invoiced for. */
  assessmentYear: number;
};

// Diocesan FY label = end year (NZ convention). Diocese FY{N} runs
// Oct {N-1} → Sept {N}, so it catches 3 months of CY{N-1} (Oct–Dec)
// and 9 months of CY{N} (Jan–Sep). Each church calendar year's annual
// quota therefore splits across two diocesan FYs.
const PRIOR_CY = 3 / 12;
const LABEL_CY = 9 / 12;

/** Split a calendar-year quota total across diocesan FYs Y1–Y3.
 *
 *  The run total is the quota for CY[baseYear+1]; successive calendar
 *  years compound by the diocese CPI default:
 *    Y1/FY{baseYear+1} = 3/12 × current 210 + 9/12 × total
 *    Y2/FY{baseYear+2} = 3/12 × total        + 9/12 × total × g
 *    Y3/FY{baseYear+3} = 3/12 × total × g    + 9/12 × total × g²
 *  where g = 1 + cpiY1Pct/100 and "current 210" is the diocese's
 *  existing annual quota figure (the prior-CY component of Y1). */
export function computeDiocesanFyTotals(
  totalPayable: number,
  current210: number,
  cpiY1Pct: number,
): { y1: number; y2: number; y3: number } {
  const growth = 1 + cpiY1Pct / 100;
  const cy1 = totalPayable;
  const cy2 = cy1 * growth;
  const cy3 = cy2 * growth;
  return {
    y1: Math.round(current210 * PRIOR_CY + cy1 * LABEL_CY),
    y2: Math.round(cy1 * PRIOR_CY + cy2 * LABEL_CY),
    y3: Math.round(cy2 * PRIOR_CY + cy3 * LABEL_CY),
  };
}
